import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchMe, getStoredToken, login as apiLogin, setAuthToken, setOnUnauthorized } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('initializing');

  const handleLogout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  useEffect(() => {
    setOnUnauthorized(() => handleLogout());
    return () => setOnUnauthorized(null);
  }, [handleLogout]);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setStatus('unauthenticated');
      return;
    }
    let cancelled = false;
    fetchMe()
      .then((response) => {
        if (cancelled) return;
        setUser(response.data.user);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled) return;
        setAuthToken(null);
        setUser(null);
        setStatus('unauthenticated');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username, password) => {
    const response = await apiLogin(username, password);
    const { token, user: nextUser } = response.data;
    setAuthToken(token);
    setUser(nextUser);
    setStatus('authenticated');
    return nextUser;
  }, []);

  const value = useMemo(() => {
    const role = user?.role;
    const isSuperAdmin = role === 'superadmin';
    const isAdmin = role === 'admin';
    return {
      user,
      status,
      login,
      logout: handleLogout,
      isAuthenticated: status === 'authenticated',
      isSuperAdmin,
      isAdmin,
      isViewer: role === 'viewer',
      // Writers (admins + superadmins) may edit domain data. Existing
      // components consume `isEditor` to gate write controls — keep it working.
      isEditor: isSuperAdmin || isAdmin,
      roleLabel: user?.roleLabel || (role ? role[0].toUpperCase() + role.slice(1) : '')
    };
  }, [user, status, login, handleLogout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
